import { realtimeService } from '@peritasai/data-access';
import {
  RealtimeEventType,
} from '@peritasai/types';
import type {
  CaseEventFilters,
  EventSubscription,
  RealtimeEvent,
} from '@peritasai/types';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Options for useRealtimeCaseUpdates hook
 */
export interface UseRealtimeCaseUpdatesOptions {
  /** Date filters for case events */
  filters?: CaseEventFilters;
  /** Event types to subscribe to (defaults to all case event types) */
  eventTypes?: RealtimeEventType[];
  /** Callback function for handling events */
  onEvent?: (event: RealtimeEvent) => void;
  /** Whether to enable the connection (defaults to true) */
  enabled?: boolean;
}

/**
 * Return type for useRealtimeCaseUpdates hook
 */
export interface UseRealtimeCaseUpdatesResult {
  /** Whether the connection is currently connected */
  connected: boolean;
  /** Error if connection failed */
  error: Error | null;
}

/**
 * React hook for subscribing to real-time case updates via SSE
 *
 * @param options - Configuration options for the real-time connection
 * @returns Connection state and error information
 */
export function useRealtimeCaseUpdates(
  options: UseRealtimeCaseUpdatesOptions = {}
): UseRealtimeCaseUpdatesResult {
  const {
    filters = {},
    eventTypes = [
      RealtimeEventType.CASE_CREATED,
      RealtimeEventType.CASE_UPDATED,
      RealtimeEventType.CASE_CANCELLED,
      RealtimeEventType.CASE_DELETED,
    ],
    onEvent,
    enabled = true,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);

  /** Delay after abort before opening new connection - lets browser release the socket */
  const RECONNECT_DELAY_MS = 100;

  // Keep onEvent callback ref up to date
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  // Memoize stringified eventTypes to prevent unnecessary reconnections
  // when array reference changes but content is the same
  const eventTypesKey = useMemo(
    () => JSON.stringify(eventTypes),
    [eventTypes]
  );

  useEffect(() => {
    // Don't connect if disabled
    if (!enabled) {
      return;
    }

    // Abort any existing connection and clear ref (close old before opening new)
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    // Clear any pending reconnect from a previous run
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Build subscriptions array from filters and event types
    const subscriptions: EventSubscription[] = eventTypes.map((eventType) => ({
      eventType,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
    }));

    // Delay before opening new connection to prevent socket leaking
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      const controller = new AbortController();
      controllerRef.current = controller;

      const connect = async () => {
        try {
          await realtimeService.connect({
            subscriptions,
            signal: controller.signal,
            onEvent: (event) => {
              onEventRef.current?.(event);
            },
            onStateChange: (state) => {
              setConnected(state.connected);
              setError(state.error);
            },
          });
        } catch (err) {
          if (err instanceof Error && err.name !== 'AbortError') {
            setError(err);
            setConnected(false);
          }
        }
      };

      connect();
    }, RECONNECT_DELAY_MS);

    // Cleanup: clear timeout, abort connection
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      setConnected(false);
      setError(null);
    };
  }, [
    enabled,
    // Include filter values in dependency array
    ...Object.values(filters),
    // Use memoized stringified eventTypes to prevent unnecessary reconnections
    eventTypesKey,
  ]);

  return { connected, error };
}

